'use client';

import { useTranslations } from 'next-intl';
import { Maximize2 } from 'lucide-react';
import { useSubscriptionLimits } from '@/features/subscription';
import { usePassPaywallStore } from '@/shared/stores/pass-paywall-store';
import { ProBadge } from '@/features/subscription/ui/ProBadge';
import { Switch } from '@/shared/ui/shadcn/switch';
import { cn } from '@/shared/lib/cn';
import { useSettingsDisplay } from './SettingsSection';

interface HiResSectionProps {
  isHires: boolean;
  onIsHiresChange: (value: boolean) => void;
}

/**
 * Hi-Res rendering toggle. Pro+ plan feature — when the active plan lacks
 * `is_feature_hires_render`, the Switch is disabled and tapping the row
 * routes to the server-driven paywall (mirrors OutputStyleCarousel's lock
 * UX). Mirrors the iOS Engine-section toggle row in
 * `apps/apple/.../RenderSettingsContent.swift`.
 */
export function HiResSection({
  isHires,
  onIsHiresChange,
}: HiResSectionProps) {
  const t = useTranslations('render.settings.hires');
  const { hideIcons } = useSettingsDisplay();

  const limits = useSubscriptionLimits();
  const hasHiresRender = limits?.has_hires_render ?? false;
  // Hi-res is a Pro perk (EntitlementFeatureMap: hires_render). A locked tap
  // upsells the Pro Pass — never the deprecated subscription paywall.
  const openPassPaywall = usePassPaywallStore((s) => s.open);

  const handleRowClick = () => {
    if (!hasHiresRender) {
      openPassPaywall('pro');
      return;
    }
    onIsHiresChange(!isHires);
  };

  const handleSwitchChange = (checked: boolean) => {
    if (!hasHiresRender) {
      openPassPaywall('pro');
      return;
    }
    onIsHiresChange(checked);
  };

  return (
    <div
      className={cn(
        'cursor-pointer hover:bg-foreground/5 active:bg-foreground/10 transition-colors'
      )}
      onClick={handleRowClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleRowClick();
        }
      }}
    >
      <div className="flex items-center gap-3 py-3 px-4">
        {!hideIcons && (
          <Maximize2
            className="h-5 w-5 text-primary flex-shrink-0"
            strokeWidth={1.5}
          />
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-card-body text-foreground whitespace-nowrap">
              {t('title')}
            </span>
            <ProBadge tier="pro" isLocked={!hasHiresRender} />
          </div>
        </div>

        <div
          // Stop the row's click handler so the user can tap the switch
          // itself without triggering an extra toggle on the parent row.
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <Switch
            checked={hasHiresRender && isHires}
            onCheckedChange={handleSwitchChange}
            disabled={!hasHiresRender}
            aria-label={t('title')}
          />
        </div>
      </div>
    </div>
  );
}
